import { IsPhaseAtLeast, IsWindowOpen, type IOEPResource, type IOEPSeasonTimeline, type OEPPhase, type OEPReviewStatus } from 'agu_oep_core';
import type { AGUScaleOption } from 'agu_app_shell';
import { CRITERION_SHORT, FileTypeLabel, Size } from '../shared/format';
import { IsReviewSubmitted, type OEPDraftFileInput } from '../reviews/reviews.vm';
import { Up } from '../shared/rows';

/** metadata/review-questions/oep-review-form.json: Sequence 6 = primary strengths, 7 = additional comments. */
const STRENGTHS_SEQ = 6;
const ADDITIONAL_SEQ = 7;
/** DecideThreadMessage's limit (agu_oep_core decisions.ts THREAD_LIMIT). */
const THREAD_LIMIT = 3;
const WRITTEN_INTRO = 'Considering the above categories, please provide written feedback in the questions below.';
/** A question's helper text from metadata: trimmed Description, or null when blank. */
const helpOf = (q: OEPReviewQuestionInput): string | null => {
    const text = (q.Description ?? '').trim();
    return text.length > 0 ? text : null;
};

export interface OEPRubricLevelInput { ID: string; Value: number; Label: string; Criteria: string }
export interface OEPReviewQuestionInput {
    ID: string; Sequence: number; Text: string; Description: string | null; Required: boolean;
    /** ReviewQuestionType starts with 'Rating Scale' (the same test ReviewResponsesComplete uses). */
    Rated: boolean;
    Levels: OEPRubricLevelInput[];
}
/** One answer as the form holds it: the chosen level's RatingValue (rated questions) and the comment. */
export interface OEPReviewAnswer { Level: number | null; Text: string }
export type OEPReviewAnswers = Readonly<Record<string, OEPReviewAnswer>>;

export interface ReviewInputs {
    Phase: OEPPhase; Timeline: IOEPSeasonTimeline; SeasonCode: string; Me: string;
    Assignment: {
        ID: string; Title: string; SectionName: string; Format: string | null; Code: string | null;
        PresenterName: string; Institution: string | null; CareerStage: string | null;
    };
    Draft: OEPDraftFileInput | null;
    Questions: OEPReviewQuestionInput[];
    Review: { ID: string; Status: OEPReviewStatus; CompletedDate: Date | null } | null;
    /** The saved answers, keyed by ReviewQuestion ID (the archive reads these). */
    Saved: OEPReviewAnswers;
    Thread: { SenderUserID: string; Body: string }[];
    ConversationID: string | null;
    /** A submit succeeded in this session. */
    JustSubmitted: boolean;
    /** Season.Resources.reviewer. */
    Resources: IOEPResource[];
}

export interface OEPRubricBlockVM {
    QuestionID: string; HeadingID: string; Name: string;
    Label: string; Help: string | null; Required: boolean;
    Options: AGUScaleOption[];
}
export interface OEPTextBlockVM { QuestionID: string; HeadingID: string; Label: string; Help: string | null; Placeholder: string; Required: boolean }

export interface ReviewEditVM {
    Section: string;
    Meta: string;
    WrittenIntro: string;
    Rubric: OEPRubricBlockVM[];
    Strengths: OEPTextBlockVM | null;
    Additional: OEPTextBlockVM | null;
    Draft: { FileID: string; FileLine: string; IsPdf: boolean } | null;
    Guidance: { Text: string; Link: IOEPResource | null };
}

export interface ReviewArchiveVM {
    Section: string;
    Meta: string;
    Scores: { Label: string; Value: string }[];
    Texts: { Label: string; Body: string }[];
    /** One line in place of scores and texts when nothing was ever submitted (implementer copy), else null. */
    Empty: string | null;
    ClosedNote: string;
    ThreadNote: string;
    Thread: { Who: string; Body: string; Mine: boolean }[];
    CanReply: boolean;
}

export type ReviewMode = 'editing' | 'submitted' | 'archive';

export interface ReviewVM {
    Mode: ReviewMode;
    Title: string;
    Edit: ReviewEditVM | null;
    /** markup.html:559-563 */
    Submitted: { Title: string; Body: string } | null;
    Archive: ReviewArchiveVM | null;
}

const present = (xs: readonly (string | null)[]): string[] => xs.filter((x): x is string => x !== null && x.length > 0);

/**
 * The review page header: the section leads (rendered uppercase), then the presenter line.
 * logic.js:644 reviewMeta "{who} · [Institution] · [Career level] · {kind} · Session: {code}"
 */
export function PresenterMeta(a: ReviewInputs['Assignment']): { Section: string; Meta: string } {
    return {
        Section: a.SectionName,
        Meta: [...present([a.PresenterName, a.Institution, a.CareerStage, a.Format]), ...(a.Code ? [`Session: ${a.Code}`] : [])].join(' · '),
    };
}

/** Criteria is a JSON string array (Review Question Rubrics.Criteria); the first entry is the level's description. */
export function FirstCriterion(raw: string): string {
    try {
        const parsed: unknown = JSON.parse(raw); // unknown: narrowed to string[] below
        return Array.isArray(parsed) && typeof parsed[0] === 'string' ? parsed[0] : raw;
    } catch {
        return raw;
    }
}

/** Every entry of a Criteria JSON string array (empty array → []); non-JSON text → the raw text as one entry. */
export function AllCriteria(raw: string): string[] {
    try {
        const parsed: unknown = JSON.parse(raw); // unknown: narrowed to string[] below
        return Array.isArray(parsed) && parsed.every((x): x is string => typeof x === 'string') ? parsed : [raw];
    } catch {
        return [raw];
    }
}

/**
 * Submit is enabled only when the server would accept it — the same test as ReviewResponsesComplete
 * (packages/OEPCore/src/rules/data.ts:104-115): every required rated question has a level and a
 * non-blank comment; every required text question is non-blank.
 */
export function CanSubmitReview(questions: readonly OEPReviewQuestionInput[], answers: OEPReviewAnswers): boolean {
    return questions.filter((q) => q.Required).every((q) => {
        const a = answers[q.ID];
        if (!a) return false;
        const text = a.Text.trim().length > 0;
        return q.Rated ? a.Level !== null && text : text;
    });
}

/**
 * Reply rule, mirroring DecideThreadMessage (decisions.ts): the Thread window is open, feedback is
 * released (Completed), the presenter started (non-empty), fewer than three, and the last one is not mine.
 */
export function CanReplyInThread(i: { Phase: OEPPhase; ReviewStatus: OEPReviewStatus | null; Thread: readonly { SenderUserID: string }[]; Me: string }): boolean {
    const n = i.Thread.length;
    return IsWindowOpen('Thread', i.Phase) && i.ReviewStatus === 'Completed' && n > 0 && n < THREAD_LIMIT && Up(i.Thread[n - 1].SenderUserID) !== Up(i.Me);
}

/**
 * A question's short name in messages: logic.js:30-61 RUBRIC[].short for the five criteria; the two
 * text questions have no short label in the prototype (implementer copy).
 */
export function QuestionShortLabel(sequence: number): string {
    if (sequence >= 1 && sequence <= CRITERION_SHORT.length) return CRITERION_SHORT[sequence - 1];
    if (sequence === STRENGTHS_SEQ) return 'Primary strengths';
    if (sequence === ADDITIONAL_SEQ) return 'Additional comments';
    return `Question ${sequence}`;
}

/** A save where some Review Responses writes were refused (implementer copy). */
export function PartialSaveMessage(failedLabels: readonly string[], total: number, firstMessage: string): string {
    const reason = firstMessage.trim().replace(/\.+$/, '');
    return `Couldn't save ${failedLabels.length} of ${total} answers (${failedLabels.join(', ')}): ${reason}. The rest were saved.`;
}

/** logic.js:672-677 archiveThreadNote. */
function threadNote(thread: readonly { SenderUserID: string }[], me: string): string {
    const n = thread.length;
    if (n === 0) return 'Your presenter can write to you here once feedback is released.';
    if (n >= THREAD_LIMIT) return 'This conversation is complete. Three messages is the limit.';
    return Up(thread[n - 1].SenderUserID) === Up(me) ? 'Sent. Your presenter can reply once more.' : 'Replying is optional, and it is the part presenters remember.';
}

function modeOf(i: ReviewInputs): ReviewMode {
    if (i.JustSubmitted) return 'submitted';
    // Editing only while the Review window is open and nothing is submitted (DecideReviewChange).
    return !IsReviewSubmitted(i.Review) && IsWindowOpen('Review', i.Phase) ? 'editing' : 'archive';
}

function edit(i: ReviewInputs): ReviewEditVM {
    const a = i.Assignment;
    const bySeq = (seq: number): OEPReviewQuestionInput | undefined => i.Questions.find((q) => q.Sequence === seq);
    const strengths = bySeq(STRENGTHS_SEQ);
    const additional = bySeq(ADDITIONAL_SEQ);
    const guidanceTitle = 'Reviewer guidance and rubric'; // logic.js:122 RESOURCES.reviewer[0]
    return {
        ...PresenterMeta(a),
        WrittenIntro: WRITTEN_INTRO,
        // markup.html:574-585
        Rubric: i.Questions.filter((q) => q.Rated).sort((x, y) => x.Sequence - y.Sequence).map((q) => ({
            QuestionID: q.ID,
            HeadingID: `oep-q-${q.ID}-label`,
            Name: `oep-q-${q.ID}`,
            Label: `${q.Sequence}. ${q.Text}`, // logic.js:31 "1. Demonstration of scientific reasoning"
            Help: helpOf(q),
            Required: q.Required,
            Options: [...q.Levels].sort((x, y) => x.Value - y.Value).map((l) => ({ Value: l.Value, Label: `${l.Label} (${l.Value})`, Description: AllCriteria(l.Criteria) })), // logic.js:62 SCALE
        })),
        // markup.html:586-590
        Strengths: strengths ? {
            QuestionID: strengths.ID, HeadingID: `oep-q-${strengths.ID}-label`,
            Label: 'What were the primary strengths of the presentation?',
            Help: helpOf(strengths),
            Placeholder: 'You clearly care about this question, and it shows in…',
            Required: strengths.Required,
        } : null,
        // markup.html:591-594
        Additional: additional ? {
            QuestionID: additional.ID, HeadingID: `oep-q-${additional.ID}-label`,
            Label: 'Additional comments to the presenter',
            Help: helpOf(additional),
            Placeholder: `Anything else that would help them before ${i.SeasonCode}…`,
            Required: additional.Required,
        } : null,
        // markup.html:612 "{{ reviewFileName }} · {{ reviewFile }}" (logic.js:264 file: 'PDF, 8.4 MB')
        Draft: i.Draft ? {
            FileID: i.Draft.FileID,
            FileLine: `${i.Draft.Name} · ${present([FileTypeLabel(i.Draft.ContentType, i.Draft.Name), Size(i.Draft.SizeBytes)]).join(', ')}`,
            IsPdf: (i.Draft.ContentType ?? '').toLowerCase() === 'application/pdf',
        } : null,
        // markup.html:614-618
        Guidance: {
            Text: 'Score all five areas against the rubric, then say what the presenter did well and what they could improve. Ratings and comments are required.',
            Link: i.Resources.find((r) => r.Title === guidanceTitle) ?? i.Resources[0] ?? null,
        },
    };
}

function archive(i: ReviewInputs): ReviewArchiveVM {
    const a = i.Assignment;
    const d = i.Timeline.Dates;
    const submitted = IsReviewSubmitted(i.Review);
    const released = IsPhaseAtLeast(i.Phase, 'FeedbackReleased');
    const bySeq = (seq: number): string => {
        const q = i.Questions.find((x) => x.Sequence === seq);
        return q ? (i.Saved[q.ID]?.Text ?? '').trim() : '';
    };
    const suggestions = bySeq(ADDITIONAL_SEQ);
    return {
        ...PresenterMeta(a),
        // markup.html:570-572, logic.js:30-61 RUBRIC[].short + SCALE
        Empty: submitted ? null : 'No feedback was submitted.',
        Scores: !submitted ? [] : i.Questions.filter((q) => q.Rated && q.Sequence >= 1 && q.Sequence <= CRITERION_SHORT.length).sort((x, y) => x.Sequence - y.Sequence).map((q) => {
            const level = i.Saved[q.ID]?.Level ?? null;
            const l = level === null ? undefined : q.Levels.find((x) => x.Value === level);
            return { Label: CRITERION_SHORT[q.Sequence - 1], Value: l ? `${l.Label} (${l.Value})` : 'Not scored' };
        }),
        // logic.js:659-663 archiveText (the third block, "Your note of encouragement", has no field in the approved rubric)
        Texts: !submitted ? [] : [
            { Label: 'What worked', Body: bySeq(STRENGTHS_SEQ) },
            ...(suggestions.length > 0 ? [{ Label: 'Suggestions before the meeting', Body: suggestions }] : []),
        ],
        // logic.js:651-653 archiveClosedNote; before the window opens, the list row's own window line (logic.js:280).
        ClosedNote: !IsPhaseAtLeast(i.Phase, 'Reviewing')
            ? `Feedback window: ${d.ReviewWindow.Label}`
            : !released
                ? `Read only. You can still reach the OEP team if something needs changing before ${d.FeedbackRelease.Label}.`
                : `Read only. The feedback window closed on ${d.FeedbackDue.Label}.`,
        ThreadNote: threadNote(i.Thread, i.Me),
        // logic.js:665-670 archiveThread
        Thread: i.Thread.map((m) => {
            const mine = Up(m.SenderUserID) === Up(i.Me);
            return { Who: mine ? 'You' : 'Your presenter', Body: m.Body, Mine: mine };
        }),
        CanReply: CanReplyInThread({ Phase: i.Phase, ReviewStatus: i.Review?.Status ?? null, Thread: i.Thread, Me: i.Me }),
    };
}

/** markup.html:556-661 / logic.js:299-320, 642-681 as one pure function. Every string cites its prototype line. */
export function BuildReviewVM(i: ReviewInputs): ReviewVM {
    const mode = modeOf(i);
    return {
        Mode: mode,
        Title: i.Assignment.Title,
        Edit: mode === 'editing' ? edit(i) : null,
        Submitted: mode === 'submitted' ? {
            Title: 'Thank you for providing feedback!',
            Body: `Your feedback will be released to the presenter on ${i.Timeline.Dates.FeedbackRelease.Label}, together with the other reviewers’ feedback. In January, you'll receive a digital letter acknowledging your volunteerism, plus any thank you note the presenter writes back.`,
        } : null,
        Archive: mode === 'archive' ? archive(i) : null,
    };
}
